
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

interface Product {
  id: number;
  name: string;
  description: string;
  price: string;
  priceLabel: string;
  image: string;
  category: string;
  categoryColor: string;
  availability: string;
  availabilityColor: string;
  rating: number;
  specs: { label: string; value: string }[];
  buttonText: string;
}

export default function WirelessSolutionsPage() {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [compareProducts, setCompareProducts] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [activeCategory, setActiveCategory] = useState('all');

  const products: Product[] = [
    {
      id: 1,
      name: 'Cisco Meraki MR46 WiFi 6 Access Point',
      description: isNL
        ? 'Enterprise WiFi 6 toegangspunt met cloudbeheer, 2,5 Gbps doorvoer en geavanceerde beveiligingsfuncties.'
        : 'Enterprise WiFi 6 access point with cloud management, 2.5 Gbps throughput, and advanced security features.',
      price: '\u20AC899,99',
      priceLabel: isNL ? 'Per stuk' : 'Per unit',
      image:
        'https://public.readdy.ai/ai/img_res/c4707e23f04fbda57fab3057efe90a52.jpg',
      category: isNL ? 'Draadloze Router' : 'Wireless Router',
      categoryColor: 'blue',
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      rating: 4.5,
      specs: [
        { label: isNL ? 'WiFi-standaard:' : 'WiFi Standard:', value: 'WiFi 6 (802.11ax)' },
        { label: isNL ? 'Max. doorvoer:' : 'Max Throughput:', value: '2,5 Gbps' },
        { label: 'MIMO:', value: '4x4 MU-MIMO' },
        { label: isNL ? 'Bereik:' : 'Coverage:', value: isNL ? '465 m\u00B2' : '5,000 sq ft' }
      ],
      buttonText: isNL ? 'Offerte Aanvragen' : 'Request a Quote'
    },
    {
      id: 2,
      name: 'Ubiquiti UniFi WiFi 6 Pro',
      description: isNL
        ? 'Hoogwaardig WiFi 6 toegangspunt met 5,3 Gbps totale doorvoer en geavanceerde beamforming-technologie.'
        : 'High-performance WiFi 6 access point with 5.3 Gbps aggregate throughput and advanced beamforming technology.',
      price: '\u20AC379,99',
      priceLabel: isNL ? 'Per stuk' : 'Per unit',
      image:
        'https://readdy.ai/api/search-image?query=Ubiquiti%20UniFi%20WiFi%206%20Pro%20wireless%20access%20point%20white%20circular%20design%20with%20blue%20LED%20ring%20ceiling%20mount%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=400&seq=ubiquiti-u6-pro-ap&orientation=squarish',
      category: isNL ? 'Toegangspunt' : 'Access Point',
      categoryColor: 'green',
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      rating: 5,
      specs: [
        { label: isNL ? 'WiFi-standaard:' : 'WiFi Standard:', value: 'WiFi 6 (802.11ax)' },
        { label: isNL ? 'Max. doorvoer:' : 'Max Throughput:', value: '5,3 Gbps' },
        { label: 'MIMO:', value: '4x4 MU-MIMO' },
        { label: isNL ? 'Voeding:' : 'Power:', value: isNL ? 'PoE+ Vereist' : 'PoE+ Required' }
      ],
      buttonText: isNL ? 'Offerte Aanvragen' : 'Request a Quote'
    },
    {
      id: 3,
      name: 'Netgear Orbi Pro WiFi 6E Tri-Band Mesh',
      description: isNL
        ? 'Enterprise tri-band WiFi 6E mesh-systeem met dedicated 6GHz backhaul en geavanceerde beveiligingsfuncties.'
        : 'Enterprise tri-band WiFi 6E mesh system with dedicated 6GHz backhaul and advanced security features.',
      price: '\u20AC1.499,99',
      priceLabel: isNL ? '3-Pack' : '3-Pack',
      image:
        'https://readdy.ai/api/search-image?query=Netgear%20Orbi%20Pro%20WiFi%206E%20mesh%20system%20three%20white%20tower%20units%20with%20blue%20LED%20indicators%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=400&seq=netgear-orbi-pro-6e&orientation=squarish',
      category: isNL ? 'Mesh-systeem' : 'Mesh System',
      categoryColor: 'purple',
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      rating: 4.5,
      specs: [
        { label: isNL ? 'WiFi-standaard:' : 'WiFi Standard:', value: 'WiFi 6E (802.11ax)' },
        { label: isNL ? 'Max. doorvoer:' : 'Max Throughput:', value: '10,8 Gbps' },
        { label: isNL ? 'Bereik:' : 'Coverage:', value: isNL ? '836 m\u00B2' : '9,000 sq ft' },
        {
          label: isNL ? 'Inbegrepen eenheden:' : 'Units Included:',
          value: isNL ? '3-Pack Systeem' : '3-Pack System'
        }
      ],
      buttonText: isNL ? 'Offerte Aanvragen' : 'Request a Quote'
    },
    {
      id: 4,
      name: 'TP-Link Archer AXE75 WiFi 6E Router',
      description: isNL
        ? 'Tri-band WiFi 6E router met 5,4 Gbps snelheid, geavanceerde QoS en uitgebreide beveiligingssuite.'
        : 'Tri-band WiFi 6E router with 5.4 Gbps speed, advanced QoS, and comprehensive security suite.',
      price: '\u20AC299,99',
      priceLabel: isNL ? 'Per stuk' : 'Per unit',
      image:
        'https://readdy.ai/api/search-image?query=TP-Link%20Archer%20AXE75%20WiFi%206E%20router%20black%20chassis%20with%20six%20external%20antennas%20and%20blue%20LED%20indicators%20front%20view%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=400&seq=tplink-archer-axe75&orientation=squarish',
      category: isNL ? 'Draadloze Router' : 'Wireless Router',
      categoryColor: 'orange',
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      rating: 4,
      specs: [
        { label: isNL ? 'WiFi-standaard:' : 'WiFi Standard:', value: 'WiFi 6E (802.11ax)' },
        { label: isNL ? 'Max. doorvoer:' : 'Max Throughput:', value: '5,4 Gbps' },
        { label: isNL ? 'Banden:' : 'Bands:', value: 'Tri-Band' },
        { label: isNL ? 'Antennes:' : 'Antennas:', value: isNL ? '6 Extern' : '6 External' }
      ],
      buttonText: isNL ? 'Offerte Aanvragen' : 'Request a Quote'
    },
    {
      id: 5,
      name: 'Aruba 7220 Mobility Controller',
      description: isNL
        ? 'Enterprise draadloze controller met ondersteuning voor maximaal 2.048 toegangspunten met geavanceerde beveiligings- en beheerfuncties.'
        : 'Enterprise wireless controller supporting up to 2,048 access points with advanced security and management features.',
      price: '\u20AC12.999,99',
      priceLabel: isNL ? 'Adviesprijs' : 'MSRP',
      image:
        'https://readdy.ai/api/search-image?query=Aruba%207220%20wireless%20mobility%20controller%20black%20rackmount%20chassis%20with%20orange%20accents%20and%20LED%20indicators%20front%20view%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=400&seq=aruba-7220-controller&orientation=squarish',
      category: 'Controller',
      categoryColor: 'red',
      availability: isNL ? 'Voorbestelling' : 'Pre-Order',
      availabilityColor: 'yellow',
      rating: 4.5,
      specs: [
        { label: isNL ? 'Max. AP\u2019s:' : 'Max APs:', value: '2.048 AP\u2019s' },
        { label: isNL ? 'Doorvoer:' : 'Throughput:', value: '40 Gbps' },
        { label: isNL ? 'Gebruikers:' : 'Users:', value: '32.000' },
        {
          label: isNL ? 'Redundantie:' : 'Redundancy:',
          value: isNL ? 'HA Ondersteuning' : 'HA Support'
        }
      ],
      buttonText: isNL ? 'Voorbestellen' : 'Pre-Order'
    },
    {
      id: 6,
      name: 'Ruckus R750 Outdoor WiFi 6 AP',
      description: isNL
        ? 'Robuust buiten WiFi 6 toegangspunt met IP67-classificatie, 4 Gbps doorvoer en geavanceerde beamforming.'
        : 'Ruggedized outdoor WiFi 6 access point with IP67 rating, 4 Gbps throughput, and advanced beamforming.',
      price: '\u20AC1.199,99',
      priceLabel: isNL ? 'Per stuk' : 'Per unit',
      image:
        'https://readdy.ai/api/search-image?query=Ruckus%20R750%20outdoor%20WiFi%206%20access%20point%20white%20weatherproof%20housing%20with%20mounting%20bracket%20IP67%20rated%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=400&seq=ruckus-r750-outdoor&orientation=squarish',
      category: isNL ? 'Buiten AP' : 'Outdoor AP',
      categoryColor: 'teal',
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      rating: 4,
      specs: [
        { label: isNL ? 'WiFi-standaard:' : 'WiFi Standard:', value: 'WiFi 6 (802.11ax)' },
        { label: isNL ? 'Max. doorvoer:' : 'Max Throughput:', value: '4 Gbps' },
        {
          label: isNL ? 'Classificatie:' : 'Rating:',
          value: isNL ? 'IP67 Weerbestendig' : 'IP67 Weatherproof'
        },
        { label: isNL ? 'Temperatuur:' : 'Temperature:', value: '-40\u00B0C tot +65\u00B0C' }
      ],
      buttonText: isNL ? 'Offerte Aanvragen' : 'Request a Quote'
    }
  ];

  const toggleCompare = (productId: number) => {
    if (compareProducts.includes(productId)) {
      setCompareProducts(compareProducts.filter(id => id !== productId));
    } else if (compareProducts.length < 4) {
      setCompareProducts([...compareProducts, productId]);
    }
  };

  const clearComparison = () => {
    setCompareProducts([]);
  };

  const getCategoryColorClass = (color: string) => {
    const colors: { [key: string]: string } = {
      blue: 'text-blue-600 bg-blue-50',
      green: 'text-green-600 bg-green-50',
      purple: 'text-purple-600 bg-purple-50',
      orange: 'text-orange-600 bg-orange-50',
      red: 'text-red-600 bg-red-50',
      teal: 'text-teal-600 bg-teal-50'
    };
    return colors[color] || 'text-gray-600 bg-gray-50';
  };

  const getAvailabilityColorClass = (color: string) => {
    const colors: { [key: string]: string } = {
      green: 'bg-green-100 text-green-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      gray: 'bg-gray-100 text-gray-800'
    };
    return colors[color] || 'bg-gray-100 text-gray-800';
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <div key={`full-${i}`} className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-fill"></i>
        </div>
      );
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-half-fill"></i>
        </div>
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <div key={`empty-${i}`} className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-line"></i>
        </div>
      );
    }

    return stars;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm">
            <a href={prefix || '/'} className="text-gray-600 hover:text-primary flex items-center cursor-pointer">
              <div className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-home-line"></i>
              </div>
              Home
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="text-gray-600 hover:text-primary cursor-pointer">
              Hardware
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a
              href={`${prefix}/products/networking`}
              className="text-gray-600 hover:text-primary cursor-pointer"
            >
              {isNL ? 'Netwerkapparatuur' : 'Networking'}
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-900 font-medium">
              {isNL ? 'Draadloze Oplossingen' : 'Wireless Solutions'}
            </span>
          </nav>
        </div>
      </div>

      {/* Header Section */}
      <div className="bg-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-start">
            <div className="max-w-4xl">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {isNL
                  ? 'Netwerkapparatuur - Draadloze Oplossingen'
                  : 'Networking Equipment - Wireless Solutions'}
              </h1>
              <p className="text-gray-600 mb-6">
                {isNL
                  ? 'Enterprise-grade draadloze netwerkapparatuur inclusief draadloze routers, toegangspunten, mesh-systemen, draadloze controllers en draadloze beveiligingsapparaten van toonaangevende fabrikanten.'
                  : 'Enterprise-grade wireless networking equipment including wireless routers, access points, mesh systems, wireless controllers, and wireless security appliances from leading manufacturers.'}
              </p>

              {/* Category and Brand Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isNL ? 'Apparatuurcategorie\u00EBn' : 'Equipment Categories'}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-router-wifi-line text-primary text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'Draadloze Routers' : 'Wireless Routers'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-wifi-line text-green-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'Toegangspunten' : 'Access Points'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-settings-3-line text-blue-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Controllers</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-share-line text-purple-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'Mesh-systemen' : 'Mesh Systems'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isNL ? 'Toonaangevende Merken' : 'Leading Brands'}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-building-line text-[#1BA0D7] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Cisco</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-router-line text-[#0559C4] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Ubiquiti</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-wifi-line text-[#0080FF] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Netgear</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-device-line text-[#00A0E4] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">TP-Link</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Category Filter Buttons */}
              <div className="flex flex-wrap gap-3 mb-6">
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`px-4 py-2 rounded-button whitespace-nowrap text-sm font-medium cursor-pointer ${
                    activeCategory === 'all'
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {isNL ? 'Alle Apparatuur' : 'All Equipment'}
                </button>
                <button
                  onClick={() => setActiveCategory('wireless-router')}
                  className={`px-4 py-2 rounded-button whitespace-nowrap text-sm font-medium cursor-pointer ${
                    activeCategory === 'wireless-router'
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {isNL ? 'Draadloze Routers' : 'Wireless Routers'}
                </button>
                <button
                  onClick={() => setActiveCategory('access-point')}
                  className={`px-4 py-2 rounded-button whitespace-nowrap text-sm font-medium cursor-pointer ${
                    activeCategory === 'access-point'
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {isNL ? 'Toegangspunten' : 'Access Points'}
                </button>
                <button
                  onClick={() => setActiveCategory('controller')}
                  className={`px-4 py-2 rounded-button whitespace-nowrap text-sm font-medium cursor-pointer ${
                    activeCategory === 'controller'
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Controllers
                </button>
                <button
                  onClick={() => setActiveCategory('mesh-system')}
                  className={`px-4 py-2 rounded-button whitespace-nowrap text-sm font-medium cursor-pointer ${
                    activeCategory === 'mesh-system'
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {isNL ? 'Mesh-systemen' : 'Mesh Systems'}
                </button>
              </div>
            </div>

            {/* View Controls */}
            <div className="flex items-center space-x-4">
              <button className="flex items-center text-gray-600 hover:text-primary cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-scales-3-line"></i>
                </div>
                {isNL ? 'Vergelijken' : 'Compare'} ({compareProducts.length})
              </button>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">{isNL ? 'Weergave:' : 'View:'}</span>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded cursor-pointer ${
                    viewMode === 'grid' ? 'text-primary bg-blue-50' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-grid-line"></i>
                  </div>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded cursor-pointer ${
                    viewMode === 'list' ? 'text-primary bg-blue-50' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-list-check"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-16">
        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-32">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">{isNL ? 'Filters' : 'Filters'}</h3>
                <button className="text-primary text-sm hover:underline cursor-pointer">
                  {isNL ? 'Alles Wissen' : 'Clear All'}
                </button>
              </div>

              {/* Equipment Type */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">
                  {isNL ? 'Apparatuurtype' : 'Equipment Type'}
                </h4>
                <div className="space-y-2">
                  {[
                    { nl: 'Draadloze Routers', en: 'Wireless Routers' },
                    { nl: 'Toegangspunten', en: 'Access Points' },
                    { nl: 'Draadloze Controllers', en: 'Wireless Controllers' },
                    { nl: 'Mesh-systemen', en: 'Mesh Systems' },
                    { nl: 'Wireless Security', en: 'Wireless Security' },
                    { nl: 'Outdoor Access Points', en: 'Outdoor Access Points' }
                  ].map((opt, idx) => (
                    <label key={idx} className="flex items-center cursor-pointer">
                      <input type="checkbox" className="filter-checkbox" />
                      <span className="ml-3 text-sm text-gray-700">{isNL ? opt.nl : opt.en}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Brand */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Merk' : 'Brand'}</h4>
                <div className="space-y-2">
                  {['Cisco', 'Ubiquiti', 'Netgear', 'TP-Link', 'Aruba', 'Ruckus'].map((brand, idx) => (
                    <label key={idx} className="flex items-center cursor-pointer">
                      <input type="checkbox" className="filter-checkbox" />
                      <span className="ml-3 text-sm text-gray-700">{brand}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* WiFi Standards */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">
                  {isNL ? 'WiFi-standaarden' : 'WiFi Standards'}
                </h4>
                <div className="space-y-2">
                  {[
                    'WiFi 6E (802.11ax)',
                    'WiFi 6 (802.11ax)',
                    'WiFi 5 (802.11ac)',
                    'WiFi 4 (802.11n)'
                  ].map((std, idx) => (
                    <label key={idx} className="flex items-center cursor-pointer">
                      <input type="checkbox" className="filter-checkbox" />
                      <span className="ml-3 text-sm text-gray-700">{std}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Frequency Bands */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">
                  {isNL ? 'Frequentiebanden' : 'Frequency Bands'}
                </h4>
                <div className="space-y-2">
                  {[
                    isNL ? 'Tri-Band (2,4/5/6 GHz)' : 'Tri-Band (2.4/5/6 GHz)',
                    isNL ? 'Dual-Band (2,4/5 GHz)' : 'Dual-Band (2.4/5 GHz)',
                    isNL ? 'Single-Band (2,4 GHz)' : 'Single-Band (2.4 GHz)',
                    isNL ? '6 GHz Ondersteuning' : '6 GHz Support'
                  ].map((band, idx) => (
                    <label key={idx} className="flex items-center cursor-pointer">
                      <input type="checkbox" className="filter-checkbox" />
                      <span className="ml-3 text-sm text-gray-700">{band}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Coverage Area */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">
                  {isNL ? 'Dekkingsgebied' : 'Coverage Area'}
                </h4>
                <div className="space-y-2">
                  {[
                    isNL ? 'Klein (Tot 140 m\u00B2)' : 'Small (Up to 1,500 sq ft)',
                    isNL ? 'Middel (140-280 m\u00B2)' : 'Medium (1,500-3,000 sq ft)',
                    isNL ? 'Groot (280-465 m\u00B2)' : 'Large (3,000-5,000 sq ft)',
                    isNL ? 'Enterprise (465+ m\u00B2)' : 'Enterprise (5,000+ sq ft)'
                  ].map((size, idx) => (
                    <label key={idx} className="flex items-center cursor-pointer">
                      <input type="checkbox" className="filter-checkbox" />
                      <span className="ml-3 text-sm text-gray-700">{size}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* MIMO Technology */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">
                  {isNL ? 'MIMO-technologie' : 'MIMO Technology'}
                </h4>
                <div className="space-y-2">
                  {['8x8 MU-MIMO', '4x4 MU-MIMO', '2x2 MIMO', 'Beamforming'].map((tech, idx) => (
                    <label key={idx} className="flex items-center cursor-pointer">
                      <input type="checkbox" className="filter-checkbox" />
                      <span className="ml-3 text-sm text-gray-700">{tech}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">
                  {isNL ? 'Beschikbaarheid' : 'Availability'}
                </h4>
                <div className="space-y-2">
                  {[
                    { nl: 'Op Voorraad', en: 'In Stock' },
                    { nl: 'Voorbestelling', en: 'Pre-Order' },
                    { nl: 'Speciale Bestelling', en: 'Special Order' }
                  ].map((opt, idx) => (
                    <label key={idx} className="flex items-center cursor-pointer">
                      <input type="checkbox" className="filter-checkbox" />
                      <span className="ml-3 text-sm text-gray-700">{isNL ? opt.nl : opt.en}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Sort and Filter Bar */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-4">
                <span className="text-gray-600">
                  {isNL ? (
                    <>
                      Toon <span>24</span> van 142 draadloze producten
                    </>
                  ) : (
                    <>
                      Showing <span>24</span> of 142 wireless products
                    </>
                  )}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Sorteren op:' : 'Sort by:'}</label>
                  <select className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-primary pr-8 cursor-pointer">
                    <option value="relevance">{isNL ? 'Relevantie' : 'Relevance'}</option>
                    <option value="price-low">{isNL ? 'Prijs: Laag naar Hoog' : 'Price: Low to High'}</option>
                    <option value="price-high">{isNL ? 'Prijs: Hoog naar Laag' : 'Price: High to Low'}</option>
                    <option value="name">{isNL ? 'Naam A-Z' : 'Name A-Z'}</option>
                    <option value="popularity">{isNL ? 'Populariteit' : 'Popularity'}</option>
                    <option value="newest">{isNL ? 'Nieuwste Eerst' : 'Newest First'}</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Toon:' : 'Show:'}</label>
                  <select className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-primary pr-8 cursor-pointer">
                    <option value="12">12</option>
                    <option value="24" selected>
                      24
                    </option>
                    <option value="48">48</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {products.map(product => (
                <div
                  key={product.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="relative">
                    <div className="h-48 overflow-hidden bg-white flex items-center justify-center p-4">
                      <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                    </div>
                    <div className="absolute top-3 left-3">
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full ${getAvailabilityColorClass(
                          product.availabilityColor
                        )}`}
                      >
                        {product.availability}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <input
                        type="checkbox"
                        className="w-5 h-5 cursor-pointer"
                        checked={compareProducts.includes(product.id)}
                        onChange={() => toggleCompare(product.id)}
                      />
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full ${getCategoryColorClass(
                          product.categoryColor
                        )}`}
                      >
                        {product.category}
                      </span>
                      <div className="text-yellow-400 flex">{renderStars(product.rating)}</div>
                    </div>
                    <h3 className="text-lg font-semibold mb-1">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-3">{product.description}</p>
                    <div className="space-y-2 mb-4">
                      {product.specs.map((spec, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-600">{spec.label}</span>
                          <span className="font-medium">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-xs text-gray-500">{product.availability}</div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="bg-gray-100 text-gray-600 px-3 py-2 rounded-button whitespace-nowrap text-sm hover:bg-gray-200 cursor-pointer">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </div>
                        </button>
                        <a
                          href={`${prefix}/contact`}
                          className="bg-primary text-white px-3 py-2 rounded-button whitespace-nowrap text-sm hover:bg-primary/90 cursor-pointer"
                        >
                          {product.buttonText}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-12">
              <div className="text-gray-600">
                {isNL ? '1-24 van 142 resultaten weergegeven' : 'Showing 1-24 of 142 results'}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
                  disabled
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </div>
                </button>
                <button className="px-3 py-2 bg-primary text-white rounded cursor-pointer">1</button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">
                  2
                </button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">
                  3
                </button>
                <span className="px-2 text-gray-500">...</span>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">
                  6
                </button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg transition-transform duration-300 z-40 ${
          compareProducts.length > 0 ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <span className="font-medium">
                {isNL ? 'Apparatuur Vergelijken' : 'Compare Equipment'} ({compareProducts.length}/4)
              </span>
              <div className="flex space-x-2">
                {compareProducts.map(id => {
                  const product = products.find(p => p.id === id);
                  return (
                    <div key={id} className="bg-gray-100 px-3 py-1 rounded text-sm">
                      {product?.name.substring(0, 20)}...
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button onClick={clearComparison} className="text-gray-600 hover:text-gray-800 cursor-pointer">
                {isNL ? 'Alles Wissen' : 'Clear All'}
              </button>
              <button className="bg-primary text-white px-6 py-2 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 cursor-pointer">
                {isNL ? 'Selectie Vergelijken' : 'Compare Selected'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
